import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Home } from 'lucide-react';

interface ErrorPageProps {
    error?: Error;
    errorInfo?: React.ErrorInfo;
    resetErrorBoundary?: () => void;
}

export function ErrorPage({ error: propError, errorInfo, resetErrorBoundary }: ErrorPageProps) {
    const navigate = useNavigate();
    const routeError = useRouteError();
    
    // Use either the prop error (from ErrorBoundary) or the route error
    const error = propError || (routeError instanceof Error ? routeError : new Error(String(routeError)));

    const handleGoBack = () => {
        if (resetErrorBoundary) {
            resetErrorBoundary();
        }
        navigate(-1);
    };

    const handleGoHome = () => {
        if (resetErrorBoundary) {
            resetErrorBoundary();
        }
        navigate('/');
    };

    // Get error message based on error type
    const getErrorMessage = () => {
        if (isRouteErrorResponse(routeError)) {
            return routeError.statusText || 'An unexpected error occurred';
        }
        return error.message || 'An unexpected error occurred';
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
                <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    
                    <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                        Oops! Something went wrong
                    </h1>
                    
                    <p className="text-slate-600 mb-6">
                        We apologize for the inconvenience. Please try again or contact support if the problem persists.
                    </p>

                    {process.env.NODE_ENV === 'development' && (error || routeError) && (
                        <div className="w-full bg-slate-50 rounded-lg p-4 mb-6">
                            <p className="text-sm font-medium text-slate-900 mb-1">Error Details:</p>
                            <p className="text-sm text-slate-600 font-mono break-all">
                                {getErrorMessage()}
                            </p>
                            {errorInfo && (
                                <div className="mt-4 text-left">
                                    <p className="text-sm font-medium text-slate-900 mb-1">Component Stack:</p>
                                    <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                                        {errorInfo.componentStack}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                        <button
                            onClick={handleGoBack}
                            className="flex items-center justify-center px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Go Back
                        </button>
                        
                        <button
                            onClick={handleGoHome}
                            className="flex items-center justify-center px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                        >
                            <Home className="w-4 h-4 mr-2" />
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
} 